import config from '../config';
import {
    LISTING_PACKAGE_ANONYM_LISTING_ID,
    PROFILE_PACKAGE_ANONYM_PROFILE_ID,
} from '../marketplace-custom-config';

const { userTypeHorseowner, anonymRiderName, anonymHorseownerName } = config;
/**
 * @param {object} user - sdk user
 * @return object sdk user
 */
export const anonymizeUser = user => {
    const anonymUserName =
        user.attributes.profile.publicData.userType === userTypeHorseowner
            ? anonymHorseownerName
            : anonymRiderName;

    return {
        ...user,
        attributes: {
            ...user.attributes,
            profile: {
                ...user.attributes.profile,
                abbreviatedName: 'BS',
                firstName: anonymUserName,
                lastName: anonymUserName,
                displayName: anonymUserName,
                isAnonym: true,
            },
        },
    };
};
/**
 * Check if a user - rider only - is anonymous
 * @param {object} currentUser - sdk user object
 * @returns {boolean}
 */
export const checkIfUserIsAnonymous = currentUser =>
    Boolean(
        (currentUser?.attributes?.profile?.publicData?.extrapackagesData || {})[
            PROFILE_PACKAGE_ANONYM_PROFILE_ID
        ]
    );
/**
 * Check if a listing - horse or rider - is anonymous
 * @param {object} currentListing - sdk listing object
 * @returns {boolean}
 */
export const checkIfListingIsAnonymous = currentListing =>
    Boolean(
        (currentListing?.attributes?.publicData?.extrapackagesData || {})[
            LISTING_PACKAGE_ANONYM_LISTING_ID
        ]
    ) ||
    /** rider listings have anonymousProfile instead of anonymousListing */
    Boolean(
        (currentListing?.attributes?.publicData?.extrapackagesData || {})[
            PROFILE_PACKAGE_ANONYM_PROFILE_ID
        ]
    );
/**
 * Check if a listing should be deanonymized for a specific user, e.g. an horse owner
 * @param {object} currentListing - sdk listing object
 * @param {object} user - sdk user object
 * @returns {boolean}
 */
export const shouldListingBeDeanonymizedForUser = (currentListing, user) =>
    (currentListing?.attributes?.publicData?.deanonymizedFor || []).some(
        id => id && id === user?.id?.uuid
    );
/**
 * Check if a user should be deanonymized for another user
 * @param {*} currentUser - The current user object
 * @param {*} otherUser - The other user object
 * @returns {boolean}
 */
export const shouldUserBeDeanonymizedForUser = (currentUser, otherUser) =>
    (otherUser?.attributes?.profile?.publicData?.deanonymizedFor || []).some(
        id => id && id === currentUser?.id?.uuid
    );

/**
 * May be used for cases when an owner looks on it's own listing
 * @param {object} currentListing - sdk listing object
 * @returns {object} - sdk listing object
 */
export const deanonymizeListing = currentListing => {
    if (checkIfListingIsAnonymous(currentListing)) {
        const isRiderListing = Boolean(currentListing.attributes.publicData.userRepresentationId);
        const anonymField = isRiderListing
            ? PROFILE_PACKAGE_ANONYM_PROFILE_ID
            : LISTING_PACKAGE_ANONYM_LISTING_ID;

        return {
            ...currentListing,
            attributes: {
                ...currentListing.attributes,
                title:
                    currentListing?.attributes?.publicData?.anonymousListingTitle ||
                    currentListing?.attributes?.title,
                publicData: {
                    ...currentListing.attributes.publicData,
                    anonymousListingTitle: null,
                    anonymousFirstName: null,
                    anonymousLastName: null,
                    extrapackagesData: {
                        ...(currentListing.attributes.publicData.extrapackagesData || {}),
                        [anonymField]: false,
                    },
                },
            },
        };
    }

    return currentListing;
};
/**
 * Deanonymize a user
 * @param {*} currentUser - The user object to deanonymize
 * @returns {*} - The deanonymized user object
 */
export const deanonymizeUser = currentUser => {
    if (checkIfUserIsAnonymous(currentUser)) {
        const attributes = currentUser?.attributes || {};
        const profile = attributes.profile || {};
        const {
            firstName: deanonFirstName,
            lastName: deanonLastName,
            displayName: deanonDisplayName,
        } = profile || {};
        const { anonymousFirstName, anonymousLastName } = profile.publicData || {};

        const [anonymFirstName = '', anonymLastName = ''] = (
            profile.publicData?.anonymousListingTitle || ''
        ).split(' ');

        const firstName = anonymousFirstName || anonymFirstName || deanonFirstName;
        const lastName = anonymousLastName || anonymLastName || deanonLastName;
        const displayName = `${firstName} ${lastName.charAt(0)}`.trim() || deanonDisplayName;

        return {
            ...currentUser,
            attributes: {
                ...attributes,
                profile: {
                    ...profile,
                    displayName,
                    firstName,
                    lastName,
                    abbreviatedName: `${firstName.charAt(0)}${lastName.charAt(0)}`,
                    publicData: {
                        ...(profile.publicData || {}),
                        extrapackagesData: {
                            ...(profile.publicData?.extrapackagesData || {}),
                            [PROFILE_PACKAGE_ANONYM_PROFILE_ID]: false,
                        },
                    },
                },
            },
        };
    }
    return currentUser;
};
/**
 * Anonymize the other party in a transaction
 * @param {*} otherParty - The other party user object
 * @returns {*} - The anonymized user object
 */

export const anonymizeOtherParty = (otherParty, currentUser, currentListing) => {
    /**
     * current user is rider
     */
    const anonymizeHorseowner = () => {
        const listingAbsentOrDeleted =
            !currentListing || !currentListing.id || currentListing?.attributes?.deleted;
        if (listingAbsentOrDeleted) {
            /**
             * We do not know if a deleted listing used to be anonym or not,
             * so in order not to reveal a horseowner, we anonymize him by default
             */

            return anonymizeUser(otherParty);
        }
        /** current listing is a horse, check if it is anonym */
        const isAnonymous = checkIfListingIsAnonymous(currentListing);

        /** check if listing has a rider id in the field */
        const isDeanonymizedForCurrentUser = shouldListingBeDeanonymizedForUser(
            currentListing,
            currentUser
        );

        if (isAnonymous && !isDeanonymizedForCurrentUser) {
            return anonymizeUser(otherParty);
        } else {
            return deanonymizeUser(otherParty);
        }
    };
    /**
     * current user is horseowner
     */
    const anonymizeRider = () => {
        const isAnonymous = checkIfUserIsAnonymous(otherParty);
        /** a rider holds horseowner id in the field */
        const isDeanonymizedForCurrentUser = shouldUserBeDeanonymizedForUser(
            currentUser,
            otherParty
        );

        if (isAnonymous && !isDeanonymizedForCurrentUser) {
            return anonymizeUser(otherParty);
        } else {
            return deanonymizeUser(otherParty);
        }
    };

    try {
        const otherPartyType = otherParty.attributes.profile.publicData.userType;
        const otherPartyIsHorseowner = otherPartyType === userTypeHorseowner;
        const otherPartyIsRider = !otherPartyIsHorseowner;
        if (otherPartyIsHorseowner) {
            return anonymizeHorseowner();
        }
        if (otherPartyIsRider) {
            return anonymizeRider();
        }
    } catch (e) {
        return deanonymizeUser(otherParty);
    }
};

export const getAnonymDisplayName = title => {
    if (typeof title !== 'string') return title;

    const [firstName, lastName] = (title || '').split(' ');
    return `${firstName} ${lastName.charAt(0)}`.trim();
};
